import React, { useEffect, useMemo, useState } from 'react'

import Drawer from '../../components/Drawer'
import GradientButton from '../../components/GradientButton'
import { useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import type { Lot } from '../../types/lot'
import type { InventoryItem } from '../../types/inventory'
import { inventoryApi } from '../../utils/inventoryApi'
import { farmDateTimeInputValue, farmTimeZone, farmWallClockToInstant } from '../../utils/farmTime'
import { TREATMENT_METHODS, treatmentApi, treatmentCostCents, type TreatmentMethod } from '../../utils/treatmentApi'
import { formatCostEventDisplayAmount } from './lotCostForm'

// Tratamientos v0 (findings #103): Lotes → Acciones → Tratamiento…
// One record per lot ticked (the lot and its same-code lots in other units);
// "Cantidad usada" is per unit.

export const TREATMENT_METHOD_KEY: Record<TreatmentMethod, TranslationKey> = {
  bath: 'lots.treatments.method.bath',
  feed: 'lots.treatments.method.feed',
  injection: 'lots.treatments.method.injection',
  other: 'lots.treatments.method.other'
}

const OTHER_PRODUCT = '__other__'

const parsePositive = (value: string): number | null | 'invalid' => {
  const trimmed = value.trim().replace(',', '.')
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 'invalid'
}

export type LotTreatmentDialogProps = {
  open: boolean
  lot: Lot
  /** Active lots with the same code in other units of the facility. */
  siblingLots: Lot[]
  unitNameById: Map<string, string>
  currency: string
  onClose: () => void
  onSaved: (count: number) => void
}

export default function LotTreatmentDialog({ open, lot, siblingLots, unitNameById, currency, onClose, onSaved }: LotTreatmentDialogProps) {
  const t = useT()
  const timeZone = farmTimeZone(lot.farmId)
  const [items, setItems] = useState<InventoryItem[]>([])
  const [lotIds, setLotIds] = useState<string[]>([lot.id])
  const [appliedAt, setAppliedAt] = useState(() => farmDateTimeInputValue(new Date().toISOString(), timeZone))
  const [method, setMethod] = useState<TreatmentMethod>('bath')
  const [productChoice, setProductChoice] = useState('')
  const [productName, setProductName] = useState('')
  const [dose, setDose] = useState('')
  const [quantity, setQuantity] = useState('')
  const [quantityUnit, setQuantityUnit] = useState('kg')
  const [duration, setDuration] = useState('')
  const [reason, setReason] = useState('')
  const [prescribedBy, setPrescribedBy] = useState('')
  const [withdrawalDays, setWithdrawalDays] = useState('0')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setLotIds([lot.id, ...siblingLots.map((sibling) => sibling.id)])
    setAppliedAt(farmDateTimeInputValue(new Date().toISOString(), timeZone))
    setError(null)
    let cancelled = false
    void inventoryApi.listAll().then((all) => {
      if (!cancelled) setItems(all.filter((item) => item.farmId === lot.farmId && item.category === 'chemical'))
    }).catch(() => undefined)
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lot.id])

  const selectedItem = useMemo(
    () => (productChoice && productChoice !== OTHER_PRODUCT ? items.find((item) => item.id === productChoice) ?? null : null),
    [items, productChoice]
  )

  useEffect(() => {
    if (selectedItem) setQuantityUnit(selectedItem.unit === 'l' ? 'L' : selectedItem.unit)
  }, [selectedItem])

  const allLots = [lot, ...siblingLots]
  const parsedQuantity = parsePositive(quantity)
  const costPerUnit = treatmentCostCents(typeof parsedQuantity === 'number' ? parsedQuantity : undefined, selectedItem)
  const totalQuantity = typeof parsedQuantity === 'number' ? parsedQuantity * lotIds.length : null

  const save = async () => {
    const name = selectedItem ? selectedItem.name : productName.trim()
    const instant = farmWallClockToInstant(appliedAt, timeZone)
    const durationValue = parsePositive(duration)
    const withdrawal = Number(withdrawalDays.trim() || '0')
    if (lotIds.length === 0) return setError(t('lots.treatments.error.lots'))
    if (!instant) return setError(t('lots.treatments.error.date'))
    if (!name) return setError(t('lots.treatments.error.product'))
    if (parsedQuantity === 'invalid' || durationValue === 'invalid') return setError(t('lots.treatments.error.number'))
    if (!reason.trim()) return setError(t('lots.treatments.error.reason'))
    if (!Number.isInteger(withdrawal) || withdrawal < 0) return setError(t('lots.treatments.error.withdrawal'))
    if (selectedItem && typeof parsedQuantity === 'number' && totalQuantity !== null && totalQuantity > selectedItem.onHand) {
      return setError(t('lots.treatments.error.stock', { onHand: String(selectedItem.onHand), unit: quantityUnit }))
    }

    setError(null)
    setSaving(true)
    let saved = 0
    let item = selectedItem
    try {
      for (const target of allLots.filter((candidate) => lotIds.includes(candidate.id))) {
        // eslint-disable-next-line no-await-in-loop
        await treatmentApi.record({
          payload: {
            farmId: target.farmId,
            lotId: target.id,
            productionUnitIds: target.productionUnitId ? [target.productionUnitId] : [],
            appliedAt: instant,
            method,
            productName: name,
            ...(item ? { inventoryItemId: item.id } : {}),
            ...(dose.trim() ? { dose: dose.trim() } : {}),
            ...(typeof parsedQuantity === 'number' ? { quantityUsed: parsedQuantity, quantityUnit } : {}),
            ...(typeof durationValue === 'number' ? { durationMinutes: Math.round(durationValue) } : {}),
            reason: reason.trim(),
            ...(prescribedBy.trim() ? { prescribedBy: prescribedBy.trim() } : {}),
            withdrawalDays: withdrawal,
            ...(notes.trim() ? { notes: notes.trim() } : {})
          },
          item,
          currency
        })
        saved += 1
        if (item && typeof parsedQuantity === 'number') item = { ...item, onHand: Math.max(0, item.onHand - parsedQuantity) }
      }
      onSaved(saved)
      onClose()
    } catch (saveError) {
      setError(saved > 0
        ? t('lots.treatments.error.partial', { saved, total: lotIds.length })
        : saveError instanceof Error && /fetch|network/i.test(saveError.message)
          ? t('lots.treatments.error.offline')
          : t('lots.treatments.error.save'))
    } finally {
      setSaving(false)
    }
  }

  const field: React.CSSProperties = { display: 'grid', gap: 4, fontSize: 13, fontWeight: 600 }
  const input: React.CSSProperties = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 400 }

  return (
    <Drawer open={open} onClose={onClose} title={t('lots.treatments.dialogTitle', { lot: lot.lotKey })}>
      <div data-testid="lot-treatment-dialog" style={{ display: 'grid', gap: 12 }}>
        <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 4 }}>
          <legend style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{t('lots.treatments.field.lots')}</legend>
          {allLots.map((candidate) => (
            <label key={candidate.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={lotIds.includes(candidate.id)}
                onChange={(event) => setLotIds((prev) => (event.target.checked ? [...prev, candidate.id] : prev.filter((id) => id !== candidate.id)))}
              />
              {candidate.lotKey} · {unitNameById.get(candidate.productionUnitId ?? '') ?? '—'}
            </label>
          ))}
        </fieldset>
        <label style={field}>
          {t('lots.treatments.field.appliedAt')}
          <input type="datetime-local" value={appliedAt} onChange={(event) => setAppliedAt(event.target.value)} style={input} data-testid="lot-treatment-applied-at" />
        </label>
        <label style={field}>
          {t('lots.treatments.field.method')}
          <select value={method} onChange={(event) => setMethod(event.target.value as TreatmentMethod)} style={input}>
            {TREATMENT_METHODS.map((option) => <option key={option} value={option}>{t(TREATMENT_METHOD_KEY[option])}</option>)}
          </select>
        </label>
        <label style={field}>
          {t('lots.treatments.field.product')}
          <select value={productChoice} onChange={(event) => setProductChoice(event.target.value)} style={input} data-testid="lot-treatment-product">
            <option value="">{t('lots.treatments.field.productPick')}</option>
            {items.map((item) => (
              <option key={item.id} value={item.id}>{t('lots.treatments.field.productOption', { name: item.name, onHand: String(item.onHand), unit: item.unit })}</option>
            ))}
            <option value={OTHER_PRODUCT}>{t('lots.treatments.field.productOther')}</option>
          </select>
          {productChoice === OTHER_PRODUCT ? (
            <input value={productName} onChange={(event) => setProductName(event.target.value)} placeholder={t('lots.treatments.field.productName')} style={input} />
          ) : null}
          {items.length === 0 ? <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b' }}>{t('lots.treatments.field.noChemicals')}</span> : null}
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ ...field, flex: '1 1 140px' }}>
            {t('lots.treatments.field.dose')}
            <input value={dose} onChange={(event) => setDose(event.target.value)} placeholder="2 % · 20 ppm" style={input} data-testid="lot-treatment-dose" />
          </label>
          <label style={{ ...field, flex: '1 1 140px' }}>
            {t('lots.treatments.field.quantity')}
            <span style={{ display: 'flex', gap: 4 }}>
              <input inputMode="decimal" value={quantity} onChange={(event) => setQuantity(event.target.value)} style={{ ...input, width: '100%' }} data-testid="lot-treatment-quantity" />
              <select value={quantityUnit} onChange={(event) => setQuantityUnit(event.target.value)} style={input} disabled={Boolean(selectedItem)}>
                {['kg', 'g', 'L', 'mL', 'pcs'].map((unit) => <option key={unit} value={unit}>{unit}</option>)}
              </select>
            </span>
          </label>
          <label style={{ ...field, flex: '1 1 120px' }}>
            {t('lots.treatments.field.duration')}
            <input inputMode="numeric" value={duration} onChange={(event) => setDuration(event.target.value)} style={input} data-testid="lot-treatment-duration" />
          </label>
        </div>
        {totalQuantity !== null && lotIds.length > 1 ? (
          <span style={{ fontSize: 12, color: '#475569' }}>{t('lots.treatments.totalQuantity', { total: String(totalQuantity), unit: quantityUnit, count: lotIds.length })}</span>
        ) : null}
        {costPerUnit !== null ? (
          <span style={{ fontSize: 12, color: '#475569' }} data-testid="lot-treatment-cost">
            {t('lots.treatments.costPreview', { cost: formatCostEventDisplayAmount(costPerUnit, (selectedItem?.currency || currency).toUpperCase()) })}
          </span>
        ) : null}
        <label style={field}>
          {t('lots.treatments.field.reason')}
          <input value={reason} onChange={(event) => setReason(event.target.value)} list="lot-treatment-reasons" style={input} data-testid="lot-treatment-reason" />
          <datalist id="lot-treatment-reasons">
            {(['fungus', 'parasites', 'bacteria', 'prevention'] as const).map((key) => <option key={key} value={t(`lots.treatments.reason.${key}` as TranslationKey)} />)}
          </datalist>
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ ...field, flex: '2 1 200px' }}>
            {t('lots.treatments.field.prescribedBy')}
            <input value={prescribedBy} onChange={(event) => setPrescribedBy(event.target.value)} style={input} data-testid="lot-treatment-prescribed-by" />
          </label>
          <label style={{ ...field, flex: '1 1 120px' }}>
            {t('lots.treatments.field.withdrawalDays')}
            <input inputMode="numeric" value={withdrawalDays} onChange={(event) => setWithdrawalDays(event.target.value)} style={input} data-testid="lot-treatment-withdrawal" />
          </label>
        </div>
        <label style={field}>
          {t('lots.treatments.field.notes')}
          <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={2} style={input} />
        </label>
        <span style={{ fontSize: 12, color: '#64748b' }}>{t('lots.treatments.help')}</span>
        {error ? <div role="alert" style={{ color: '#b91c1c', fontWeight: 700, fontSize: 13 }}>{error}</div> : null}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <GradientButton size="sm" variant="secondary" onClick={onClose}>{t('common.cancel')}</GradientButton>
          <GradientButton size="sm" variant="success" onClick={() => void save()} disabled={saving} loading={saving} data-testid="lot-treatment-save">
            {t('common.save')}
          </GradientButton>
        </div>
      </div>
    </Drawer>
  )
}
